import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import ArrowCircleLeftOutlinedIcon from "@mui/icons-material/ArrowCircleLeftOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import axios from "axios";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import { authHeader } from "auth/authHeader";

import useConsultationContext from "hook/useConsultationContext";

import {
  CONSULTATION_STAGE,
  resolvePatientConsultationStage,
} from "./utils/resolvePatientConsultationStage";

import { JoinVideoConsultation2 } from "./JoinVideoConsultationInternal";

import "./appointments.css";
import "./consultations.css";

const APPOINTMENT_REQUEST_TIMEOUT_MS =
  15_000;

const UNAVAILABLE_MESSAGES = {
  PHYSICAL_APPOINTMENT:
    "This is a physical appointment. Online consultation is not available.",

  APPOINTMENT_CANCELED:
    "This appointment has been canceled.",

  APPOINTMENT_COMPLETED:
    "This consultation has already been completed.",

  INVALID_APPOINTMENT_STATUS:
    "This appointment is not currently available for online consultation.",

  INVALID_APPOINTMENT_SLOT_TIME:
    "The consultation time is unavailable or invalid.",

  UNSUPPORTED_PROFESSION_TYPE:
    "Consultation timing is not configured for this health professional.",

  CONSULTATION_HARD_ENDED:
    "The consultation time has ended.",
};

const getBackendUrl = (path) => {
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL;

  if (!backendUrl) {
    throw new Error(
      "REACT_APP_BACKEND_URL is not configured.",
    );
  }

  const normalizedBaseUrl =
    backendUrl.replace(
      /\/+$/,
      "",
    );

  const normalizedPath =
    String(path).replace(
      /^\/+/,
      "",
    );

  return `${normalizedBaseUrl}/${normalizedPath}`;
};

const getResponseMessage = (
  error,
  fallbackMessage,
) =>
  error?.response?.data?.message ||
  error?.response?.data?.error
    ?.message ||
  error?.message ||
  fallbackMessage;

const extractAppointment = (
  response,
) => {
  const payload =
    response?.data?.data;

  if (Array.isArray(payload)) {
    return payload[0] ?? null;
  }

  return payload ?? null;
};

/*
 * =========================================================
 * PROVIDER DISPLAY NAME
 * =========================================================
 *
 * Appointment Details already owns the
 * full appointment record including Doctor.
 *
 * We only carry the static display name
 * forward into the consultation flow.
 *
 * Timing/access authority still comes from
 * the consultation context API.
 */
const resolveProviderName = (
  appointment,
) => {
  const provider =
    appointment?.Doctor ||
    appointment?.doctor ||
    null;

  return (
    provider?.displayName ||
    [
      provider?.prefix,
      provider?.name,
    ]
      .filter(Boolean)
      .join(" ") ||
    provider?.doctorName ||
    "Health professional"
  );
};

const AppointmentConsultation =
  () => {
    const navigate =
      useNavigate();

    const params =
      useParams();

    /*
     * Keep the legacy `id` fallback temporarily.
     * Remove it after every route uses
     * `:appointmentId`.
     */
    const appointmentId =
      params.appointmentId ||
      params.id ||
      null;

    const appointmentRequestIdRef =
      useRef(0);

    const [
      appointment,
      setAppointment,
    ] = useState(null);

    const [
      appointmentLoading,
      setAppointmentLoading,
    ] = useState(
      Boolean(appointmentId),
    );

    const [
      appointmentRefreshing,
      setAppointmentRefreshing,
    ] = useState(false);

    const [
      appointmentError,
      setAppointmentError,
    ] = useState(null);

    const {
      consultationContext,

      loading:
        consultationLoading,

      error:
        consultationError,

      refresh:
        refreshConsultationContext,
    } =
      useConsultationContext({
        appointmentId,

        enabled: Boolean(
          appointmentId,
        ),
      });

    /*
     * =========================================================
     * APPOINTMENT DETAILS
     * =========================================================
     */

    const loadAppointment =
      useCallback(
        async ({
          silent = false,
        } = {}) => {
          if (
            !appointmentId
          ) {
            const missingIdError =
              new Error(
                "Appointment ID is unavailable.",
              );

            setAppointmentError(
              missingIdError,
            );

            setAppointmentLoading(
              false,
            );

            setAppointmentRefreshing(
              false,
            );

            return null;
          }

          const requestId =
            appointmentRequestIdRef
              .current + 1;

          appointmentRequestIdRef.current =
            requestId;

          if (silent) {
            setAppointmentRefreshing(
              true,
            );
          } else {
            setAppointmentLoading(
              true,
            );
          }

          setAppointmentError(
            null,
          );

          try {
            const response =
              await axios.get(
                getBackendUrl(
                  `appointment/${encodeURIComponent(
                    appointmentId,
                  )}`,
                ),
                {
                  headers:
                    authHeader(),

                  timeout:
                    APPOINTMENT_REQUEST_TIMEOUT_MS,
                },
              );

            const loadedAppointment =
              extractAppointment(
                response,
              );

            if (
              !loadedAppointment
            ) {
              throw new Error(
                "Appointment details are unavailable.",
              );
            }

            if (
              appointmentRequestIdRef
                .current ===
              requestId
            ) {
              setAppointment(
                loadedAppointment,
              );
            }

            return loadedAppointment;
          } catch (error) {
            if (
              appointmentRequestIdRef
                .current ===
              requestId
            ) {
              setAppointmentError(
                error,
              );
            }

            return null;
          } finally {
            if (
              appointmentRequestIdRef
                .current !==
              requestId
            ) {
              return;
            }

            if (silent) {
              setAppointmentRefreshing(
                false,
              );
            } else {
              setAppointmentLoading(
                false,
              );
            }
          }
        },
        [appointmentId],
      );

    /*
     * Fetch a fresh appointment whenever
     * the route ID changes.
     *
     * Previous route data is not reused.
     */
    useEffect(() => {
      setAppointment(null);

      setAppointmentError(
        null,
      );

      loadAppointment({
        silent: false,
      });

      return () => {
        /*
         * Ignore responses belonging
         * to the previous route or
         * an unmounted component.
         */
        appointmentRequestIdRef.current +=
          1;
      };
    }, [
      appointmentId,
      loadAppointment,
    ]);

    /*
     * =========================================================
     * CONSULTATION STATE
     * =========================================================
     */

    const consultationStage =
      useMemo(
        () =>
          resolvePatientConsultationStage(
            consultationContext,
          ),
        [
          consultationContext,
        ],
      );

    const appointmentFatalError =
      Boolean(
        appointmentError &&
          !appointment,
      );

    const pageLoading =
      Boolean(
        appointmentId,
      ) &&
      ((!appointment &&
        appointmentLoading) ||
        (!consultationContext &&
          consultationLoading));

    /*
     * =========================================================
     * PROVIDER DISPLAY DATA
     * =========================================================
     *
     * Static display information only.
     */
    const providerName =
      useMemo(
        () =>
          resolveProviderName(
            appointment,
          ),
        [appointment],
      );

    /*
     * =========================================================
     * NAVIGATION
     * =========================================================
     */

    const handleGoBack =
      () => {
        navigate(
          "/appointments",
        );
      };

    /*
     * Appointment Details
     *     ↓
     * Waiting Room
     *
     * Carry only static UI information.
     *
     * Do NOT carry timing/joinability
     * information here.
     */
    const handleEnterWaitingRoom =
      () => {
        if (
          !appointmentId
        ) {
          return;
        }

        navigate(
          `/appointment-consultation/${encodeURIComponent(
            appointmentId,
          )}/waiting`,
          {
            state: {
              providerName,
            },
          },
        );
      };

    /*
     * =========================================================
     * RETRY / REFRESH
     * =========================================================
     */

    const handleAppointmentRetry =
      async () => {
        await loadAppointment({
          silent: false,
        });
      };

    const handleConsultationRetry =
      async () => {
        try {
          await refreshConsultationContext();
        } catch (error) {
          /*
           * useConsultationContext
           * stores the error.
           */
        }
      };

    const handleRefreshAll =
      async () => {
        await Promise.allSettled([
          loadAppointment({
            silent:
              Boolean(
                appointment,
              ),
          }),

          refreshConsultationContext({
            silent: true,
          }),
        ]);
      };

    /*
     * =========================================================
     * CONSULTATION PRESENTATION
     * =========================================================
     */

    const renderConsultationState =
      () => {
        if (
          !consultationContext
        ) {
          return (
            <Alert severity="warning">
              Consultation
              information is
              unavailable.
            </Alert>
          );
        }

        switch (
          consultationStage
        ) {
          case CONSULTATION_STAGE.SCHEDULED:

          case CONSULTATION_STAGE.WAITING:

          case CONSULTATION_STAGE.JOINABLE:

          case CONSULTATION_STAGE.HARD_ENDED:

          case CONSULTATION_STAGE.UNAVAILABLE:
            return (
              <JoinVideoConsultation2
                appointmentData={
                  appointment
                }
                consultationContext={
                  consultationContext
                }
                consultationStage={
                  consultationStage
                }
                refreshConsultationContext={
                  refreshConsultationContext
                }
                onEnterWaitingRoom={
                  handleEnterWaitingRoom
                }
              />
            );

          default:
            return (
              <Alert severity="warning">
                The consultation
                state is
                unavailable.
              </Alert>
            );
        }
      };

    /*
     * =========================================================
     * PAGE
     * =========================================================
     */

    return (
      <Box
        sx={{
          width: "100%",

          boxSizing:
            "border-box",

          p: {
            xs: 1,
            sm: 2,
          },
        }}
      >
        <Stack spacing={2}>
          <Box
            sx={{
              display: "flex",

              alignItems:
                "center",

              justifyContent:
                "space-between",

              gap: 1,

              flexWrap:
                "wrap",
            }}
          >
            <Button
              onClick={
                handleGoBack
              }
              startIcon={
                <ArrowCircleLeftOutlinedIcon />
              }
            >
              Back to Appointments
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={
                <RefreshOutlinedIcon />
              }
              disabled={
                appointmentLoading ||
                appointmentRefreshing ||
                consultationLoading
              }
              onClick={
                handleRefreshAll
              }
            >
              {appointmentRefreshing
                ? "Refreshing..."
                : "Refresh"}
            </Button>
          </Box>

          {pageLoading && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
              }}
            >
              <LinearProgress />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1.5,

                  textAlign:
                    "center",
                }}
              >
                Loading
                consultation...
              </Typography>
            </Paper>
          )}

          {!pageLoading &&
            appointmentFatalError && (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={
                      handleAppointmentRetry
                    }
                  >
                    Try again
                  </Button>
                }
              >
                {getResponseMessage(
                  appointmentError,
                  "Unable to load appointment details.",
                )}
              </Alert>
            )}

          {!pageLoading &&
            appointment &&
            appointmentError && (
              <Alert severity="warning">
                The latest
                appointment
                information could
                not be loaded.
                Displaying the
                currently available
                information.
              </Alert>
            )}

          {!pageLoading &&
            consultationError && (
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={
                      handleConsultationRetry
                    }
                  >
                    Try again
                  </Button>
                }
              >
                {getResponseMessage(
                  consultationError,
                  "Unable to verify consultation access.",
                )}
              </Alert>
            )}

          {!pageLoading &&
            !appointmentFatalError &&
            !consultationError &&
            appointment &&
            renderConsultationState()}
        </Stack>
      </Box>
    );
  };

export default AppointmentConsultation;